import { createSchema } from 'react-hook-form-auto'
import gql from 'graphql-tag'
import { useQuery, useSubscription } from "@apollo/client/react";

import { createDeleteUpdater } from '../pintor'
import { useMutation } from '../graphql/notificatingMutation'

export const PlaceSchema = createSchema('place', {
  name: {
    type: 'string',
    required: true,
    max: 255
  }
})

const placeFragment = gql`
  fragment PlaceFragment on Place {
    id
    createdAt
    name
    groupOrder
    hiddenGroups
  }
`

const PLACES_GET = gql`
  query PlacesList {
    collection: places {
      ...PlaceFragment
    }
  }
  ${placeFragment}
`

const PLACE_INGREDIENTS_GET = gql`
  query PlaceIngredients($placeId: String!) {
    collection: placeIngredients(placeId: $placeId) {
      id
      name
    }
  }
`

const PLACES_SUB = gql`
  subscription PlaceUpdate {
    placeChange {
      ...PlaceFragment
    }
  }
  ${placeFragment}
`

const PLACE_UPDATE = gql`
  mutation PlaceUpdate($input: PlaceInput!) {
    placeUpdate(input: $input) {
      ...PlaceFragment
    }
  }
  ${placeFragment}
`

const PLACE_DELETE = gql`
  mutation PlaceDelete($id: ID!) {
    placeDelete(id: $id)
  }
`

const PLACE_DELETED_SUB = gql`
  subscription PlaceDeletedSub {
    placeDeleted
  }
`

// Not createUpdater: its add path uses cache.modify with the
// aliased key ('collection'), which is a no-op on ROOT_QUERY
const dataUpdater = ({ client, data }) => {
  const place = data.data.placeChange
  if (!place)
    return

  let cached = null
  try {
    cached = client.cache.readQuery({ query: PLACES_GET })
  } catch (error) {
    // Query never ran here: start an empty collection
  }
  const collection = cached && cached.collection || []

  const exists = collection.some(other => other.id == place.id)
  const newCollection = exists ?
    collection.map(other => other.id == place.id ? place : other) :
    [ ...collection, place ]

  newCollection.sort((a, b) => a.name.localeCompare(b.name))

  client.cache.writeQuery({
    query: PLACES_GET,
    data: { collection: newCollection }
  })
}

export const usePlaceUpdate = () => {
  const { data, loading } = useSubscription(PLACES_SUB, {
    onData: dataUpdater
  })
  const placeChange = data && data.placeChange

  return [ placeChange, loading ]
}

export const usePlaceDelete = (options = {}) =>
  useMutation(PLACE_DELETE, options)

const dataDeleter = createDeleteUpdater({
  subscriptionName: 'placeDeleted',
  collectionKey: 'places',
  typename: 'Place'
})

export const usePlaceDeleted = () => {
  const { data, loading } = useSubscription(PLACE_DELETED_SUB, {
    onData: dataDeleter
  })
  const placeDeleted = data && data.placeDeleted

  return [ placeDeleted, loading ]
}

// Groups sorted by the place's aisle order. Groups not present
// in the order yet go last, alphabetically.
export const orderGroups = (place, groups = []) => {
  const order = (place && place.groupOrder || []).map(String)
  const index = group => order.indexOf(String(group.id))

  const known = groups
    .filter(group => index(group) != -1)
    .sort((a, b) => index(a) - index(b))
  const unknown = groups
    .filter(group => index(group) == -1)
    .sort((a, b) => a.name.localeCompare(b.name))

  return [ ...known, ...unknown ]
}

export const isGroupHidden = (place, group) => {
  const hidden = (place && place.hiddenGroups || []).map(String)

  return hidden.indexOf(String(group.id)) != -1
}

export const usePlaces = () => {
  const { data, loading } = useQuery(PLACES_GET)
  const collection = data && data.collection

  return [ collection, loading ]
}

export const Place = {
  name: 'place',
  schema: PlaceSchema,
  fetch: PLACES_GET,
  sub: PLACES_SUB,
  update: PLACE_UPDATE,
  delete: PLACE_DELETE,
  fragment: placeFragment,
  fetchIngredients: PLACE_INGREDIENTS_GET,
  orderGroups,
  isGroupHidden,
  usePlaces,
  usePlaceUpdate,
  usePlaceDelete,
  usePlaceDeleted
}
